import React, { Fragment, useMemo, useState } from 'react';

import { Box, IconButton, ListItem, ListItemButton, ListItemText, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { ArrowForwardIos } from '@mui/icons-material';

import { Button } from '../Button';
import { ProgressIcon } from '../Icons';
import { useEditableSectionsContext } from './editable-sections.context';
import {
  EditableSectionRowFormWrapper, EditableSectionRowFormWrapperProps
} from './EditableSectionRowFormWrapper.component';
import { useIsMobile, useUpBreakpoint } from '@uplift/hooks';

type RowKindProps = {
  kind: 'editable-row';
  form: React.ReactNode;
  actions?: never;
  label: React.ReactNode;
  editLabel?: React.ReactNode;
  onClick?: never;
  showOnSectionEdit?: never;
  editTip?: React.ReactNode;
  mobileEditButton?: React.ReactNode;
} | {
  kind: 'custom';
  form?: never;
  actions?: React.ReactNode;
  label?: React.ReactNode;
  editLabel?: React.ReactNode;
  onClick?: never;
  showOnSectionEdit?: boolean;
  editTip?: never;
  mobileEditButton?: never;
} | {
  kind: 'mobile-button';
  form?: never;
  actions?: never;
  label: React.ReactNode;
  editLabel?: React.ReactNode;
  onClick: () => void;
  children?: never;
  showOnSectionEdit?: boolean;
  editTip?: never;
  mobileEditButton?: never;
};

export type EditableSectionRowProps = {
  rowKey: string;
  children?: React.ReactNode;
  hideLabel?: boolean;
  divider?: boolean;
  hideRowDisplay?: boolean;
  className?: string;
  mobileClassName?: string;
  editButton?: React.ReactElement<{ onClick: () => void; disabled?: boolean }>;
  editButtonText?: string;
  editButtonClassName?: string;
  editModalNoPadding?: boolean;
  hideEditButton?: boolean;
  disabledMobileFormModal?: boolean;
  testId?: string;
  rowFormWrapperProps?: Partial<Omit<EditableSectionRowFormWrapperProps, 'onCancel'>>;
} & RowKindProps;

// TODO jss-to-tss-react codemod: usages of this hook outside of this file will not be converted.
export const useEditableSectionRowStyles = makeStyles()((theme) => ({
  mobileListItemRoot: {
    padding: theme.spacing(2),
  },
  mobileListItemRootLarge: {
    padding: theme.spacing(3, 2),
  },
  noPadding: {
    padding: 0,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  divider: {
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  buttonLoader: {
    position: 'absolute',
    left: 0,
    top: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }
}));

const useSectionEditRow = (rowKey: string) => {
  const [ loading, setLoading ] = useState(false);
  const editableSectionsState = useEditableSectionsContext();

  const isEditRow = editableSectionsState?.editKey === rowKey;
  const onEditRow = async () => {
    if (editableSectionsState.preprocessEditKeyChange) {
      setLoading(true);
      await editableSectionsState.preprocessEditKeyChange(rowKey);
      setLoading(false);
    }
    editableSectionsState.setEditKey(rowKey);
  };
  const onCancelEdit = () => {
    editableSectionsState.setEditKey(null);
  };

  return { isEditRow, onEditRow, onCancelEdit, loading, tipWidth: editableSectionsState.tipWidth };
};

export const EditableSectionRow: React.FC<EditableSectionRowProps> = ({ children, ...props }) => {
  const isMobile = useIsMobile('sm');

  if (props.kind === 'mobile-button') {
    return isMobile ? <MobileRow {...props} /> : null;
  }

  return isMobile ? (
    <MobileRow {...props}>
      {children ?? null}
    </MobileRow>
  ) : (
    <DesktopRow {...props}>
      {children ?? null}
    </DesktopRow>
  );
};

type RowProps = EditableSectionRowProps;

const MobileRow: React.FC<RowProps> = ({ children, form, rowKey, label, editLabel, hideEditButton, disabledMobileFormModal, rowFormWrapperProps, kind, actions, hideLabel, onClick, divider, hideRowDisplay, editModalNoPadding, mobileClassName, mobileEditButton }) => {
  const { classes } = useEditableSectionRowStyles();
  const { isEditRow, onEditRow, onCancelEdit } = useSectionEditRow(rowKey);

  const listItem = useMemo(() => {
    const content = (
      <Box flex={1}>
        {!hideLabel && label && (
          <Box mb={0.5}>
            <Typography>{label}</Typography>
          </Box>
        )}
        {children}
      </Box>
    );

    if (kind === 'editable-row' && !hideEditButton) {
      return (
        <ListItemButton
          classes={{ root: classes.mobileListItemRoot }}
          className={mobileClassName}
          role="row"
          onClick={onEditRow}
          component="li"
          divider={divider}
          id={rowKey}
        >
          {content}
          {mobileEditButton ?? (
            <IconButton color="secondary" onClick={onEditRow}>
              <ArrowForwardIos fontSize="small" />
            </IconButton>
          )}
        </ListItemButton>
      );
    }

    if (kind === 'mobile-button') {
      return (
        <ListItemButton
          classes={{ root: classes.mobileListItemRoot }}
          className={mobileClassName}
          role="row"
          component="li"
          onClick={onClick}
          divider={divider}
          id={rowKey}
        >
          <ListItemText>
            {label}
          </ListItemText>
          <IconButton color="secondary" onClick={onClick}>
            <ArrowForwardIos fontSize="small" />
          </IconButton>
        </ListItemButton>
      );
    }

    return (
      <ListItem
        classes={{ root: actions ? classes.mobileListItemRoot : classes.mobileListItemRootLarge }}
        className={mobileClassName}
        role="row"
        divider={divider}
        id={rowKey}
      >
        {content}
        {actions && (
          <div>
            {actions}
          </div>
        )}
      </ListItem>
    );
  }, [ actions, children, classes, divider, hideEditButton, hideLabel, kind, label, mobileClassName, mobileEditButton, onClick, onEditRow, rowKey ]);

  const editModal = useMemo(() => {
    if (isEditRow && !disabledMobileFormModal && kind === 'editable-row') {
      return(
        <EditableSectionRowFormWrapper
          onCancel={onCancelEdit}
          rowLabel={editLabel || label}
          noContentPadding={editModalNoPadding}
          {...rowFormWrapperProps}
        >
          {form}
        </EditableSectionRowFormWrapper>
      );
    }

    return null;
  }, [ disabledMobileFormModal, form, isEditRow, kind, label, onCancelEdit, rowFormWrapperProps, editLabel, editModalNoPadding ]);

  return (
    <Fragment>
      {editModal}
      {!hideRowDisplay && listItem}
    </Fragment>
  );
};

const DesktopRow: React.FC<RowProps> = ({ children, form, rowKey, label, editLabel, editButton, editButtonText = 'Edit', editButtonClassName, hideEditButton, rowFormWrapperProps, actions, kind, divider, hideLabel, hideRowDisplay, className, editTip, testId='editable-section-row' }) => {
  const { classes, cx } = useEditableSectionRowStyles();
  const isMdUp = useUpBreakpoint('md');
  const { isEditRow, onEditRow, onCancelEdit, loading, tipWidth } = useSectionEditRow(rowKey);

  const editTipDisplay = useMemo(() => {
    if (editTip && isEditRow && kind === 'editable-row') {
      return <Box mb={2}>{editTip}</Box>;
    }

    return null;
  }, [ editTip, isEditRow, kind ]);

  const content = useMemo(() => {
    if (isEditRow && kind === 'editable-row') {
      return (
        <EditableSectionRowFormWrapper
          onCancel={onCancelEdit}
          rowLabel={editLabel || label}
          {...rowFormWrapperProps}
        >
          {isMdUp ? null : editTipDisplay}
          {form}
        </EditableSectionRowFormWrapper>
      );
    }

    return children;
  }, [ isEditRow, kind, children, onCancelEdit, editLabel, label, rowFormWrapperProps, editTipDisplay, form, isMdUp ]);

  const action = useMemo(() => {
    if (actions) {
      return (
        <Box className={classes.actions}>
          {actions}
        </Box>
      );
    }

    if (kind === 'editable-row' && !isEditRow && !hideEditButton) {
      if (React.isValidElement(editButton)) {
        return React.cloneElement(editButton, {
          onClick: () => {
            editButton.props?.onClick?.();
            if (!editButton.props.disabled) {
              onEditRow();
            }
          }
        });
      }

      return (
        <Box position="relative" width="fit-content" className={editButtonClassName}>
          <Button
            disabled={loading}
            color="secondary"
            onClick={onEditRow}
            style={{ visibility: loading ? 'hidden' : 'initial' }}
            data-testid="editable-section-row-edit-button"
          >
            {editButtonText}
          </Button>
          {loading && (
            <Box className={classes.buttonLoader}>
              <ProgressIcon />
            </Box>
          )}
        </Box>
      );
    }

    return null;
  }, [ actions, kind, isEditRow, hideEditButton, classes.actions, classes.buttonLoader, editButton, loading, onEditRow, editButtonClassName, editButtonText ]);

  if (hideRowDisplay && !isEditRow) {
    return null;
  }

  return (
    (
      <Box id={rowKey} role="row" display="flex" alignItems="center" py={3} pl={3} pr={2} className={cx(className, { [classes.divider]: divider })} position="relative" data-testid={testId}>
        <Box flex={1} display="flex">
          {(!hideLabel || isEditRow) && label && (
            <Box width="175px" fontWeight={isEditRow ? 600 : undefined}>
              <Typography>{isEditRow ? editLabel || label : label}</Typography>
            </Box>
          )}
          <Box flex={1}>
            {content}
          </Box>
        </Box>
        {action}
        {editTipDisplay && (
          isMdUp ? (
            <Box position="absolute" left="calc(100% + 24px)" top="16px" style={{ width: tipWidth ? `${tipWidth}px` : undefined }}>
              {editTipDisplay}
            </Box>
          ) : null
        )}
      </Box>
    )
  );
};
